import type { ClientIntent, ServerMessage } from '@ringmaster/shared';
import type { ConnectOptions, GameDriver } from './GameDriver.js';
import { loadSession } from '../net/session.js';
import { netBase } from '../discord/embedded.js';

/**
 * LiveSocketDriver — the REAL WebSocket transport (Wave 2).
 *
 * Implements GameDriver exactly like MockDriver so the UI (useRoom + the Host/
 * Play routes) is unchanged: connect → open a ws to `/ws`, JSON-encode outbound
 * ClientIntents, JSON-decode inbound ServerMessages and fan them out to every
 * subscriber, and transparently auto-reconnect with backoff, re-sending REJOIN
 * from the stored session so a dropped phone rebinds to the same seat.
 *
 * Message shapes are the frozen wire protocol from `@ringmaster/shared`:
 *   send:    ClientIntent   (already validated shape; server re-validates with zod)
 *   receive: ServerMessage  (JOINED | PUBLIC_STATE | PRIVATE_STATE | NARRATION |
 *                            TIMER | ERROR)
 */

/** Reconnect backoff schedule (ms). After the last entry we stop trying. */
const BACKOFF_MS = [1000, 2000, 4000, 4000, 4000];

export class LiveSocketDriver implements GameDriver {
  private subscribers = new Set<(msg: ServerMessage) => void>();
  private ws: WebSocket | null = null;
  private opts: ConnectOptions = { role: 'store' };

  /** Buffer of intents queued while the socket is still opening. */
  private outbox: ClientIntent[] = [];

  /** Reconnect bookkeeping. */
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private closedByUser = false;
  /** True once the socket has opened at least once — distinguishes the first
   *  connect (UI drives CREATE/JOIN/REJOIN) from a reconnect (we auto-REJOIN). */
  private hasConnected = false;

  /**
   * Latest identity for reconnect. `connect` seeds this from opts; a JOINED
   * message refreshes it so a rejoin uses the freshest server-minted token.
   */
  private code: string | null = null;
  private sessionToken: string | null = null;

  connect(opts: ConnectOptions): void {
    this.opts = opts;
    this.closedByUser = false;
    this.code = opts.code ?? null;
    this.sessionToken = opts.sessionToken ?? null;
    // If caller passed a code but no token, try the persisted session.
    if (this.code && !this.sessionToken) {
      const stored = loadSession(this.code);
      if (stored) this.sessionToken = stored.sessionToken;
    }
    this.open();
  }

  send(intent: ClientIntent): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(intent));
    } else {
      // Not open yet (or mid-reconnect) — queue and flush on open.
      this.outbox.push(intent);
    }
  }

  onMessage(cb: (msg: ServerMessage) => void): () => void {
    this.subscribers.add(cb);
    return () => this.subscribers.delete(cb);
  }

  /**
   * Whether the underlying socket is currently OPEN. Not part of the GameDriver
   * contract — the broadcast (/stage) route polls it to render a subtle
   * "reconnecting" glyph while a dropped stage socket loops its backoff. Returns
   * false before the first open and during any reconnect gap.
   */
  isConnected(): boolean {
    return this.ws != null && this.ws.readyState === WebSocket.OPEN;
  }

  close(): void {
    this.closedByUser = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.detachSocket();
    this.ws?.close();
    this.ws = null;
    this.subscribers.clear();
    this.outbox = [];
  }

  // ── internals ──────────────────────────────────────────────────────────────

  private open(): void {
    const url = wsBaseUrl();
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.addEventListener('open', this.onOpen);
    ws.addEventListener('message', this.onWsMessage);
    ws.addEventListener('close', this.onClose);
    ws.addEventListener('error', this.onError);
  }

  private detachSocket(): void {
    const ws = this.ws;
    if (!ws) return;
    ws.removeEventListener('open', this.onOpen);
    ws.removeEventListener('message', this.onWsMessage);
    ws.removeEventListener('close', this.onClose);
    ws.removeEventListener('error', this.onError);
  }

  private onOpen = (): void => {
    const isReconnect = this.hasConnected;
    this.hasConnected = true;
    // Reset backoff on a successful connection.
    this.attempt = 0;

    if (this.opts.role === 'stage' && this.code) {
      // A spectator/broadcast surface: on EVERY open (including the first) send
      // WATCH so the server binds this socket as a playerless Stage and resends
      // full public state. There is no seat/session to REJOIN — WATCH is the
      // whole handshake, and it re-binds cleanly on each reconnect.
      this.rawSend({ type: 'WATCH', code: this.code });
    } else if (isReconnect && this.code && this.sessionToken) {
      // On a RECONNECT (we already opened once and hold a session), silently
      // REJOIN so the server rebinds this socket to the existing seat and resends
      // full state. On the FIRST connect the UI drives CREATE_ROOM / JOIN_ROOM /
      // REJOIN itself, so we don't send anything unsolicited.
      this.rawSend({
        type: 'REJOIN',
        code: this.code,
        sessionToken: this.sessionToken,
      });
      // Re-claim the stage on the NEW socket: stage status is per-socket, and
      // the UI's one-time CLAIM_STAGE can't know the socket was replaced. The
      // server also restores the claim by player id, so this is the belt to
      // that braces — either alone keeps the voice on.
      if (this.opts.claimStage) this.rawSend({ type: 'CLAIM_STAGE' });
    }

    // Flush anything the UI queued before the socket was ready.
    const pending = this.outbox;
    this.outbox = [];
    for (const intent of pending) this.rawSend(intent);
  };

  private onWsMessage = (ev: MessageEvent): void => {
    let msg: ServerMessage;
    try {
      msg = JSON.parse(String(ev.data)) as ServerMessage;
    } catch {
      return; // ignore non-JSON frames
    }
    // Track identity for reconnect: the server mints the authoritative token.
    if (msg.type === 'JOINED') {
      this.code = msg.code;
      this.sessionToken = msg.sessionToken;
    }
    for (const cb of this.subscribers) cb(msg);
  };

  private onClose = (): void => {
    this.detachSocket();
    this.ws = null;
    if (this.closedByUser) return;
    this.scheduleReconnect();
  };

  private onError = (): void => {
    // An error is followed by a close event; reconnect logic lives there. We
    // only need to make sure a socket that errored before opening still closes.
    try {
      this.ws?.close();
    } catch {
      /* ignore */
    }
  };

  private scheduleReconnect(): void {
    if (this.closedByUser) return;
    // A stage/broadcast surface reconnects FOREVER (a TV or Activity viewer must
    // recover from any server blip unattended). It needs only a code to WATCH —
    // no session token — and never gives up: past the backoff schedule it loops
    // the last (longest) delay indefinitely.
    const isStage = this.opts.role === 'stage';
    if (isStage) {
      if (!this.code) return; // nothing to watch yet
    } else {
      if (this.attempt >= BACKOFF_MS.length) return; // give up after max tries
      // Auto-reconnect only once we hold a session to rejoin with; a socket that
      // never joined a room (store / pre-join) has nothing to restore — the UI
      // drives a fresh CREATE/JOIN there instead.
      if (!(this.code && this.sessionToken)) return;
    }
    const delay = BACKOFF_MS[this.attempt] ?? BACKOFF_MS[BACKOFF_MS.length - 1]!;
    // Cap the attempt counter at the schedule length so a long-lived stage socket
    // doesn't overflow — it just keeps using the final backoff delay.
    if (this.attempt < BACKOFF_MS.length) this.attempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.open();
    }, delay);
  }

  private rawSend(intent: ClientIntent): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(intent));
    }
  }
}

/**
 * Resolve the WebSocket base URL from the current origin (proxied in dev). Inside
 * a Discord Activity the connection is routed through the `/.proxy` prefix
 * (netBase()); outside Discord netBase() is '' and the URL is unchanged.
 */
export function wsBaseUrl(): string {
  if (typeof window === 'undefined') return 'ws://localhost:8787/ws';
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${window.location.host}${netBase()}/ws`;
}
